import type { FieldData } from 'rc-field-form/lib/interface'
import type { FormInstance } from 'antd'

import type { ErrorResponse, ErrorCodes } from './types/api'

export const isValidationIssue = (data: unknown): data is ErrorResponse & { code: ErrorCodes } =>
  !!data && typeof data === 'object' && (data as ErrorResponse).code === 422

// antd form expects field errors to have the shape of FieldData with required
// name and errors properties. This type ensures that, making it easy to pass
// field errors to form.setFields
export type FormFieldError<Values = unknown> = FieldData<Values> & Required<Pick<FieldData<Values>, 'name' | 'errors'>>

// Convert marshmallow validation errors into antd form field error format
export const getFieldErrors = <Values>(data: unknown): FormFieldError<Values>[] | null => {
  if (isValidationIssue(data) &&  data.errors) {
    const fieldErrors: FormFieldError<Values>[] = []

    const processErrors = (errors: Record<string, unknown>, path: string[] = []) => {
      for (const [field, value] of Object.entries(errors)) {
        const currentPath = [...path, field]
        if (Array.isArray(value)) {
          // Leaf node with error messages
          fieldErrors.push({
            name: currentPath.length === 1 ? currentPath[0] : currentPath,
            errors: value as string[],
          } as FormFieldError<Values>)
        } else if (typeof value === 'object' && value !== null) {
          // Nested object, recurse
          processErrors(value as Record<string, unknown>, currentPath)
        }
      }
    }

    for (const key of Object.keys(data.errors)) {
      // key is usually 'json' for JSON body validation errors but can also be
      // e.g., query, etc. We squash all into a flat list
      const errors = data.errors[key] as Record<string, unknown>
      processErrors(errors)
    }

    return fieldErrors
  }

  return null
}

export const handleFieldErrors = (
  data: unknown,
  form: FormInstance,
): boolean => {
  const fieldErrors = getFieldErrors<FormInstance>(data)
  if (fieldErrors) {
    form.setFields(fieldErrors)
    return true
  }
  return false
}
